import gql from 'graphql-tag';
import { getApolloClient } from 'src/clientService';
import {
	GetCompanySettings,
	updateCompany,
} from 'src/_shared/api/graphql/custom/company';
import { createDepartment } from 'src/_shared/api/graphql/custom/departments/createDepartmentGraphql';
import { createSubCompany } from 'src/_shared/api/graphql/custom/sub-company/createSubCompanyGraphql';
import { createTieredBonus } from 'src/_shared/api/graphql/custom/tiered-bonuses/createTieredBonusGraphql';
import { queryTieredBonusesByCompanyIdIndex } from 'src/_shared/api/graphql/custom/tiered-bonuses/queryTieredBonusesByCompanyIdIndexGraphql';
import { createUserGroup } from 'src/_shared/api/graphql/custom/userGroups/createUserGroupGraphql';
import { queryUserGroupsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/userGroups/queryUserGroupsByCompanyIdGraphql';

// The shared queryDepartmentsByCompanyIdIndex omits externalDepartmentId,
// which the duplicate must keep (job-sync mapping rules key off it).
const queryDepartmentsPaged = `
  query QueryDepartmentsByCompanyIdIndex($companyId: ID!, $first: Int, $after: String) {
    queryDepartmentsByCompanyIdIndex(companyId: $companyId, first: $first, after: $after) {
      items {
        id
        name
        keywords
        active
        externalDepartmentId
      }
      nextToken
    }
  }
`;

// The shared querySubCompanyByCompanyIdIndex omits nextToken, so pagination
// would silently stop after one page.
const querySubCompaniesPaged = `
  query QuerySubCompanyByCompanyIdIndex($companyId: ID!, $first: Int, $after: String) {
    querySubCompanyByCompanyIdIndex(companyId: $companyId, first: $first, after: $after) {
      items {
        id
        keywords
        name
      }
      nextToken
    }
  }
`;

// Fields of the GetCompanySettings result that must NOT be copied onto the
// duplicate. The duplicate is a data-only clone for integration testing:
// no identity, no credentials, no assets, no source-tenant record references.
const COMPANY_COPY_DENYLIST = [
	'__typename',
	// identity
	'id',
	'name',
	'host',
	'ssoGoogleDomain',
	// credentials / external wiring
	'otherAPIKey',
	'sftpFolderName',
	'senderEmailAddress',
	'metabaseReports',
	// references into the source tenant
	'selectedAdmins',
	'referralBonusNotificationRecipients',
	'referredReportTo',
	'partnerClientId',
	'partnerHostId',
	'partnerHost',
	'preStartUserGroupId', // remapped to the copied group instead
	// partner/server-derived
	'whiteLabelSettings',
	'showPoweredByErin',
	// assets (S3 pointers) — data-only clone
	'logo',
	'shareLogo',
	'symbol',
	'background',
	'errorImage',
	'favicon',
	'socialImage',
	'internalMobilityImage',
	'dashboardSearchBG',
	'appStoreUrls',
	// out of scope for the clone
	'recognitionSettings',
	// operational state
	'disableSite',
	'resetPopup',
];

async function fetchAllPages(client, queryString, resultKey, companyId) {
	const items = [];
	let after = null;
	do {
		const { data } = await client.query({
			query: gql(queryString),
			variables: { companyId, first: 100, after },
			fetchPolicy: 'network-only',
		});
		const page = data[resultKey];
		items.push(...(page?.items ?? []));
		after = page?.nextToken ?? null;
	} while (after);

	return items;
}

function buildCompanyCopyInput(sourceCompany, newCompanyId, groupIdMap) {
	const input = {};
	for (const [key, value] of Object.entries(sourceCompany)) {
		if (COMPANY_COPY_DENYLIST.includes(key)) continue;
		if (value === null || value === undefined) continue;
		input[key] = value;
	}

	input.id = newCompanyId;

	const mappedGroupId = groupIdMap[sourceCompany.preStartUserGroupId];
	if (mappedGroupId) input.preStartUserGroupId = mappedGroupId;

	return input;
}

// The live UpdateCompanyInput drifts from both the stale docs schemas and the
// GetCompanySettings selection set, and AppSync's validation error does not
// name the offending field ("The variables input contains a field that is not
// defined for input object type 'UpdateCompanyInput'"), so pruning can't react
// to the error. Instead, introspect the live input type and only send fields
// it declares.
const updateCompanyInputFieldsQuery = gql`
	query UpdateCompanyInputFields {
		__type(name: "UpdateCompanyInput") {
			inputFields {
				name
			}
		}
	}
`;

async function fetchUpdatableCompanyFieldNames(client) {
	try {
		const { data } = await client.query({
			query: updateCompanyInputFieldsQuery,
			fetchPolicy: 'network-only',
		});
		const fields = (data?.__type?.inputFields ?? []).map(
			(field) => field.name
		);
		return fields.length > 0 ? new Set(fields) : null;
	} catch (error) {
		console.warn(
			'duplicateCompany: UpdateCompanyInput introspection failed; sending the unfiltered input',
			error
		);
		return null;
	}
}

async function updateCompanyWithLiveSchemaFields(client, input) {
	const updatableFields = await fetchUpdatableCompanyFieldNames(client);
	const filtered = { ...input };
	if (updatableFields) {
		for (const key of Object.keys(filtered)) {
			if (!updatableFields.has(key)) {
				console.warn(
					`duplicateCompany: dropping '${key}' — not in the live UpdateCompanyInput`
				);
				delete filtered[key];
			}
		}
	}

	return client.mutate({
		mutation: gql(updateCompany),
		variables: { input: filtered },
	});
}

class DuplicateCompanyError extends Error {
	constructor(message, phase, newCompanyId) {
		super(message);
		this.phase = phase;
		this.newCompanyId = newCompanyId;
	}
}

// Creates a new company named newName and copies the source company's
// data-level settings plus its departments (including ATS linkage), user
// groups, subcompanies (minus logos), and unarchived tiered bonus templates.
// createCompany must be the withListCompanies onCreateCompany mutation so the
// admin-console table cache picks up the new row.
export async function duplicateCompany({
	sourceCompanyId,
	newName,
	createCompany,
	onProgress = () => {},
}) {
	const client = getApolloClient();

	onProgress('reading the source company');
	let sourceCompany;
	let departments;
	let userGroups;
	let subCompanies;
	let tieredBonuses;
	try {
		[sourceCompany, departments, userGroups, subCompanies, tieredBonuses] =
			await Promise.all([
				client
					.query({
						query: GetCompanySettings,
						variables: { id: sourceCompanyId },
						fetchPolicy: 'network-only',
					})
					.then(({ data }) => data.getCompany),
				fetchAllPages(
					client,
					queryDepartmentsPaged,
					'queryDepartmentsByCompanyIdIndex',
					sourceCompanyId
				),
				fetchAllPages(
					client,
					queryUserGroupsByCompanyIdIndex,
					'queryUserGroupsByCompanyIdIndex',
					sourceCompanyId
				),
				fetchAllPages(
					client,
					querySubCompaniesPaged,
					'querySubCompanyByCompanyIdIndex',
					sourceCompanyId
				),
				fetchAllPages(
					client,
					queryTieredBonusesByCompanyIdIndex,
					'queryTieredBonusesByCompanyIdIndex',
					sourceCompanyId
				),
			]);
		if (!sourceCompany) throw new Error('Source company not found');
	} catch (error) {
		throw new DuplicateCompanyError(error.message, 'reading the source company');
	}

	onProgress('creating the company');
	let newCompanyId;
	try {
		const { data } = await createCompany({ input: { name: newName } });
		newCompanyId = data.createCompany.id;
	} catch (error) {
		throw new DuplicateCompanyError(error.message, 'creating the company');
	}

	onProgress('copying departments, groups, subcompanies & bonuses');
	const groupIdMap = {};
	try {
		await Promise.all([
			...departments.map((department) =>
				client.mutate({
					mutation: gql(createDepartment),
					variables: {
						input: {
							companyId: newCompanyId,
							name: department.name,
							active: !!department.active,
							keywords: department.keywords,
							externalDepartmentId: department.externalDepartmentId,
						},
					},
				})
			),
			...userGroups.map((group) =>
				client
					.mutate({
						mutation: gql(createUserGroup),
						variables: {
							input: {
								companyId: newCompanyId,
								name: group.name,
								active: !!group.active,
								currency: group.currency,
								measurement: group.measurement,
								keywords: group.keywords,
								languageCode: group.languageCode,
							},
						},
					})
					.then(({ data }) => {
						groupIdMap[group.id] = data.createUserGroup.id;
					})
			),
			...subCompanies.map((subCompany) =>
				client.mutate({
					mutation: gql(createSubCompany),
					variables: {
						input: {
							companyId: newCompanyId,
							name: subCompany.name,
							keywords: subCompany.keywords,
						},
					},
				})
			),
			...tieredBonuses
				.filter((bonus) => !bonus.archived)
				.map((bonus) =>
					client.mutate({
						mutation: gql(createTieredBonus),
						variables: {
							input: {
								companyId: newCompanyId,
								name: bonus.name,
								tiers: bonus.tiers,
							},
						},
					})
				),
		]);
	} catch (error) {
		throw new DuplicateCompanyError(
			error.message,
			'copying departments, groups, subcompanies & bonuses',
			newCompanyId
		);
	}

	onProgress('copying company settings');
	try {
		await updateCompanyWithLiveSchemaFields(
			client,
			buildCompanyCopyInput(sourceCompany, newCompanyId, groupIdMap)
		);
	} catch (error) {
		throw new DuplicateCompanyError(
			error.message,
			'copying company settings',
			newCompanyId
		);
	}

	return { id: newCompanyId, name: newName };
}
